import { Widget, WidgetAtivo } from './types';

/**
 * Carrega os scripts dos widgets de terceiro.
 *
 * Location: src/lib/widgets
 * Why: **nenhum script é montado por concatenação de texto.** Essa é a decisão
 * que torna a feature segura por construção, e não por confiar na validação.
 *
 * O caminho óbvio seria montar a tag como string e inserir com `innerHTML`:
 *
 *     el.innerHTML = `<script>gtag('config','${id}')</script>`   // NÃO
 *
 * Aí o identificador vira **código-fonte**, e basta uma aspa para escapar dele
 * — numa página onde o jogador digita a senha e paga. O backend valida o
 * formato, mas depender só disso é apostar que nenhum bug futuro afrouxe a
 * regra.
 *
 * Aqui o identificador é sempre um **valor**: entra como argumento de função
 * (`gtag('config', id)`) ou como propriedade de objeto. O motor de JavaScript
 * nunca o interpreta como código, então nem um valor malformado escapa.
 *
 * Nas URLs, `encodeURIComponent`. Nos elementos, `createElement` e atributos —
 * nunca `innerHTML`.
 */

/** Já carregados nesta página, para não duplicar. */
const carregados = new Set<Widget>();

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: ((...args: unknown[]) => void) & { callMethod?: unknown; queue?: unknown[]; loaded?: boolean; version?: string; push?: unknown };
    _fbq?: unknown;
    ttq?: any;
    TiktokAnalyticsObject?: string;
    oaiq?: ((...args: unknown[]) => void) & { q?: unknown[] };
    clarity?: ((...args: unknown[]) => void) & { q?: unknown[] };
    $crisp?: unknown[];
    CRISP_WEBSITE_ID?: string;
    Tawk_API?: Record<string, unknown>;
    Tawk_LoadStart?: Date;
    pixelId?: string;
    adsbygoogle?: unknown[];
  }
}

/** Insere um `<script src>` sem tocar em `innerHTML`. */
export function carregarExterno(src: string, atributos: Record<string, string> = {}): void {
  const s = document.createElement('script');
  s.async = true;
  s.src = src;

  for (const [chave, valor] of Object.entries(atributos)) {
    s.setAttribute(chave, valor);
  }

  document.head.appendChild(s);
}

/**
 * Google: GA4 e Google Ads compartilham o `gtag`.
 *
 * Carregar o `gtag.js` duas vezes quebraria os dois, então a base é montada
 * uma vez e cada produto só acrescenta o próprio `config`.
 */
function garantirGtag(primeiroId: string): void {
  if (window.gtag) return;

  carregarExterno(
    `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(primeiroId)}`,
  );

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
}

const CARREGADORES: Record<Widget, (config: Record<string, string>) => void> = {
  ga4: ({ measurementId }) => {
    garantirGtag(measurementId);
    // O id vai como **valor**, nunca dentro do código-fonte.
    window.gtag!('config', measurementId);
  },

  google_ads: ({ conversionId }) => {
    garantirGtag(conversionId);
    window.gtag!('config', conversionId);
  },

  meta_pixel: ({ pixelId }) => {
    if (!window.fbq) {
      const n: any = function (...args: unknown[]) {
        n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
      };
      n.queue = [];
      n.loaded = true;
      n.version = '2.0';
      n.push = n;
      window.fbq = n;
      window._fbq = n;

      carregarExterno('https://connect.facebook.net/en_US/fbevents.js');
    }

    window.fbq!('init', pixelId);
    window.fbq!('track', 'PageView');
  },

  tiktok_pixel: ({ pixelId }) => {
    if (!window.ttq) {
      window.TiktokAnalyticsObject = 'ttq';
      const ttq: any = [];
      ttq.methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie'];
      ttq.setAndDefer = (obj: any, metodo: string) => {
        obj[metodo] = (...args: unknown[]) => obj.push([metodo, ...args]);
      };
      ttq.methods.forEach((m: string) => ttq.setAndDefer(ttq, m));
      ttq.load = (id: string) => {
        ttq._i = ttq._i || {};
        ttq._i[id] = [];
        ttq._t = ttq._t || {};
        ttq._t[id] = +new Date();
        carregarExterno(
          `https://analytics.tiktok.com/i18n/pixel/events.js?sdkid=${encodeURIComponent(id)}&lib=ttq`,
        );
      };
      window.ttq = ttq;
    }

    window.ttq.load(pixelId);
    window.ttq.page();
  },

  openai_ads: ({ pixelId }) => {
    if (!window.oaiq) {
      const q: any = function (...args: unknown[]) {
        q.q.push(args);
      };
      q.q = [];
      window.oaiq = q;

      carregarExterno('https://bzrcdn.openai.com/sdk/oaiq.min.js');
    }

    /*
     * O pixel da OpenAI assume consentimento a menos que se diga o contrário.
     * Como só chegamos aqui depois do aceite, declaramos explicitamente — a
     * chamada documenta a decisão para quem for auditar.
     */
    window.oaiq!('consent', true);
    window.oaiq!('init', { pixelId });
  },

  utmify: ({ pixelId }) => {
    // O script lê o id de um global; é a interface que eles publicam.
    window.pixelId = pixelId;

    carregarExterno('https://cdn.utmify.com.br/scripts/pixel/pixel.js');
    carregarExterno('https://cdn.utmify.com.br/scripts/utms/latest.js', {
      'data-utmify-prevent-subid': '',
    });
  },

  clarity: ({ projectId }) => {
    if (!window.clarity) {
      const c: any = function (...args: unknown[]) {
        c.q.push(args);
      };
      c.q = [];
      window.clarity = c;
    }

    carregarExterno(`https://www.clarity.ms/tag/${encodeURIComponent(projectId)}`);
  },

  crisp: ({ websiteId }) => {
    window.$crisp = [];
    window.CRISP_WEBSITE_ID = websiteId;

    carregarExterno('https://client.crisp.chat/l.js');
  },

  tawk: ({ propertyId, widgetId }) => {
    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();

    carregarExterno(
      `https://embed.tawk.to/${encodeURIComponent(propertyId)}/${encodeURIComponent(widgetId)}`,
      { crossorigin: '*' },
    );
  },
};

/**
 * Carrega um widget, uma vez só.
 *
 * A guarda importa mais do que parece: o React em modo estrito monta os
 * componentes duas vezes em desenvolvimento, e sem ela o pixel contaria cada
 * visita em dobro — um erro que só apareceria como número errado no relatório
 * do lojista, sem nada quebrar.
 */
export function carregarWidget({ widget, config }: WidgetAtivo): void {
  if (carregados.has(widget)) return;

  /*
   * `CARREGADORES[widget]` enxerga a cadeia de protótipos.
   *
   * O nome do widget vem do payload da API, que só emite valores do enum —
   * mas este arquivo não deve depender disso. Com `constructor` ou `toString`
   * no lugar do nome, a busca devolveria uma função herdada de `Object` e nós
   * a chamaríamos com a configuração do lojista. Não dá execução de código
   * (o `try` embaixo segura), e é exatamente o tipo de brecha que vira uma
   * quando o arquivo mudar. `hasOwnProperty` fecha a porta agora.
   */
  if (!Object.prototype.hasOwnProperty.call(CARREGADORES, widget)) return;

  const carregador = CARREGADORES[widget];

  if (typeof carregador !== 'function') return;

  try {
    carregador(config);
    carregados.add(widget);
  } catch {
    /*
     * Widget que falha não derruba a loja.
     *
     * Um erro dentro do script de um terceiro é problema dele; deixar a
     * exceção subir tiraria a página do ar por causa de um pixel.
     */
  }
}

/**
 * Dispara a visualização de página nos widgets que rastreiam navegação.
 *
 * Existe porque a loja é uma SPA: trocar de rota não recarrega a página, e
 * sem este disparo o relatório do lojista mostraria só a primeira página de
 * cada sessão.
 */
export function registrarVisualizacao(ativos: WidgetAtivo[]): void {
  for (const { widget, config } of ativos) {
    if (!carregados.has(widget)) continue;

    try {
      if (widget === 'ga4') window.gtag?.('event', 'page_view');
      if (widget === 'meta_pixel') window.fbq?.('track', 'PageView');
      if (widget === 'tiktok_pixel') window.ttq?.page();
      if (widget === 'openai_ads') window.oaiq?.('track', 'page_viewed');
    } catch {
      // Idem: relatório errado é melhor que loja fora do ar.
    }
  }
}

/** Só para teste: limpa a memória de quem já carregou. */
export function _limparCarregados(): void {
  carregados.clear();
}
